/**
 * WorkspaceShell — layout map-céntrico del espacio de trabajo (primitiva S.UI.P1).
 *
 * Ranura de contenido principal central permanente (`principal`: mapa en P2,
 * tabla de cotejo en P3) + paneles acoplables (izquierda, derecha y abajo) +
 * ranura («slot») para la barra de estado. Componente presentacional sin lógica
 * de negocio ni orquestador; único estado interno: las dimensiones de los
 * agarres de redimensionado (`P2RED-S1-1`, ficha 280-500 px + tabla, §11.3
 * CRITERIOS_SHELL). Montado en P2 (`Step2Page`) y P3 (`Step3CotejaPage`).
 * `panelDer` = ficha del elemento (RED-1=a). Renombrado `mapa` → `principal` en
 * T2PR (03-jul-2026, `OBS-SHELL-RANURAS`); el `data-testid="workspace-mapa"`
 * se conserva hasta la rebanada visual (lo referencian las máscaras del spec
 * E2E `visual-regression.spec.ts` y su cambio exige rebaseline).
 *
 * Responsividad «auto-ocultable» en pantallas estrechas: el panel izquierdo se
 * oculta por CSS (media query inyectada) por debajo del punto de ruptura, sin
 * lógica JS. Skin DS v2 (Space Grotesk + JetBrains Mono + paleta PTEL); sin
 * glow ni box-shadow (regla dura).
 *
 * @version 1.0.0
 */

import { useCallback, useRef, useState } from 'react';
import type { CSSProperties, FC, PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { colors, typography } from '@/design-system/v2';

// ============================================================================
// DESIGN TOKENS (DS v2)
// ============================================================================

const COLORS = {
  bgRaiz: colors.bg.primary,
  bgPanel: colors.bg.secondary,
  bgBarra: colors.bg.tertiary,
  fgSecundario: colors.fg.secondary,
  borde: colors.border.DEFAULT,
} as const;

// Anclas de clase para la hoja de estilo responsive inyectada.
const CLASE_RAIZ = 'ptel-ws-shell';
const CLASE_CUERPO = 'ptel-ws-cuerpo';
const CLASE_PANEL_IZQ = 'ptel-ws-panel-izq';

// Valores por defecto (presentacionales, sobreescribibles por props).
const ANCHO_PANEL_IZQ = 320;
const ALTO_PANEL_INF = 220;
const BREAKPOINT_ESTRECHO = 768;

// Ficha del elemento (panel derecho, RED-1=a): rango normativo §11.3.
const ANCHO_PANEL_DER = 320;
const ANCHO_PANEL_DER_MIN = 280;
const ANCHO_PANEL_DER_MAX = 500;
// Tabla inferior canónica: rango pragmático (agarre visible, §11.3).
const ALTO_PANEL_INF_MIN = 140;
const ALTO_PANEL_INF_MAX = 480;
// Grosor del agarre de redimensionado (px).
const GROSOR_AGARRE = 6;

// ============================================================================
// INYECCIÓN CSS RESPONSIVE (auto-ocultable, sin lógica JS)
// ============================================================================

// Puntos de ruptura ya inyectados (evita duplicar <style> por instancia).
const breakpointsInyectados = new Set<number>();

/**
 * Inyecta una hoja de estilo (una vez por punto de ruptura) que oculta el panel
 * izquierdo y colapsa el cuerpo a una sola columna por debajo del ancho dado.
 * Guardado por `typeof document` para no romper en SSR / entornos sin DOM.
 */
function inyectarEstilosResponsive(breakpoint: number): void {
  if (typeof document === 'undefined') return;
  if (breakpointsInyectados.has(breakpoint)) return;
  const style = document.createElement('style');
  style.setAttribute('data-ptel-ws-bp', String(breakpoint));
  style.textContent =
    `@media (max-width:${breakpoint}px){` +
    `.${CLASE_RAIZ}[data-ws-bp="${breakpoint}"] .${CLASE_PANEL_IZQ}{display:none;}` +
    `.${CLASE_RAIZ}[data-ws-bp="${breakpoint}"] .${CLASE_CUERPO}{grid-template-columns:1fr;}` +
    `}`;
  document.head.appendChild(style);
  breakpointsInyectados.add(breakpoint);
}

// ============================================================================
// TYPES
// ============================================================================

export interface WorkspaceShellProps {
  /** Contenido principal central (permanente): mapa, tabla u otro lienzo. Ranura requerida. */
  principal: ReactNode;
  /** Panel acoplable izquierdo (p. ej. capas, herramientas). Opcional. */
  panelIzq?: ReactNode;
  /** Panel acoplable derecho: ficha del elemento (RED-1=a). Redimensionable 280-500 px. Opcional. */
  panelDer?: ReactNode;
  /** Panel acoplable inferior (p. ej. tabla de atributos). Redimensionable con agarre. Opcional. */
  panelInf?: ReactNode;
  /** Ranura de barra de estado (coords en vivo, contadores). Opcional. */
  barraEstado?: ReactNode;
  /** Mostrar el panel izquierdo (presentacional, sin estado interno). Def: true. */
  panelIzqVisible?: boolean;
  /** Mostrar el panel derecho (ficha). Def: true. */
  panelDerVisible?: boolean;
  /** Mostrar el panel inferior (presentacional, sin estado interno). Def: true. */
  panelInfVisible?: boolean;
  /** Ancho del panel izquierdo en píxeles. Def: 320. */
  anchoPanelIzq?: number;
  /** Ancho inicial del panel derecho (px, no controlado; el agarre lo ajusta 280-500). Def: 320. */
  anchoPanelDerInicial?: number;
  /** Alto inicial del panel inferior (px, no controlado; el agarre lo ajusta 140-480). Def: 220. */
  altoPanelInf?: number;
  /** Punto de ruptura (px) por debajo del cual el panel izq. se auto-oculta. Def: 768. */
  breakpointEstrecho?: number;
  /** Alto del contenedor. Def: '100%'. */
  alto?: string | number;
  /** Clase CSS adicional. */
  className?: string;
}

// ============================================================================
// STYLES
// ============================================================================

const raizStyle = (alto: string | number): CSSProperties => ({
  display: 'grid',
  gridTemplateRows: '1fr auto auto', // cuerpo · panel inferior · barra de estado
  height: typeof alto === 'number' ? `${alto}px` : alto,
  minHeight: 0,
  width: '100%',
  overflow: 'hidden',
  background: COLORS.bgRaiz,
  color: COLORS.fgSecundario,
  fontFamily: typography.fontFamily.sans,
});

const cuerpoStyle = (
  anchoIzq: number,
  conPanelIzq: boolean,
  anchoDer: number,
  conPanelDer: boolean,
): CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: [
    conPanelIzq ? `${anchoIzq}px` : null,
    '1fr',
    conPanelDer ? `${GROSOR_AGARRE}px ${anchoDer}px` : null,
  ]
    .filter(Boolean)
    .join(' '),
  minHeight: 0,
  overflow: 'hidden',
});

const panelDerStyle: CSSProperties = {
  minWidth: 0,
  minHeight: 0,
  overflow: 'auto',
  background: COLORS.bgPanel,
  borderLeft: `1px solid ${COLORS.borde}`,
};

// Agarre visible (§11.3): franja con línea central, cursor de redimensionado.
const agarreStyle = (eje: 'x' | 'y'): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: eje === 'x' ? 'col-resize' : 'row-resize',
  touchAction: 'none',
  userSelect: 'none',
  background: COLORS.bgBarra,
  ...(eje === 'y' ? { height: `${GROSOR_AGARRE}px`, width: '100%' } : {}),
});

const agarreGripStyle = (eje: 'x' | 'y'): CSSProperties => ({
  background: COLORS.borde,
  borderRadius: '2px',
  ...(eje === 'x' ? { width: '2px', height: '32px' } : { width: '32px', height: '2px' }),
});

// ============================================================================
// HOOK INTERNO — dimensión redimensionable por arrastre (agarre)
// ============================================================================

/**
 * Dimensión con arrastre acotado [min, max]. El delta se mide desde el punto
 * de agarre: arrastrar hacia el interior del panel (izquierda para la ficha,
 * arriba para la tabla) lo agranda. Estado no controlado (esqueleto S1-1).
 */
function useDimensionArrastrable(
  eje: 'x' | 'y',
  inicial: number,
  min: number,
  max: number,
): { valor: number; onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => void } {
  const [valor, setValor] = useState(() => Math.min(max, Math.max(min, inicial)));
  const valorRef = useRef(valor);
  valorRef.current = valor;

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const origen = eje === 'x' ? e.clientX : e.clientY;
      const base = valorRef.current;
      const mover = (ev: globalThis.PointerEvent | MouseEvent) => {
        const delta = origen - (eje === 'x' ? ev.clientX : ev.clientY);
        setValor(Math.min(max, Math.max(min, base + delta)));
      };
      const soltar = () => {
        window.removeEventListener('pointermove', mover);
        window.removeEventListener('pointerup', soltar);
      };
      window.addEventListener('pointermove', mover);
      window.addEventListener('pointerup', soltar);
    },
    [eje, min, max],
  );

  return { valor, onPointerDown };
}

const panelIzqStyle: CSSProperties = {
  minWidth: 0,
  overflow: 'auto',
  background: COLORS.bgPanel,
  borderRight: `1px solid ${COLORS.borde}`,
};

const principalStyle: CSSProperties = {
  position: 'relative',
  minWidth: 0,
  minHeight: 0,
  overflow: 'hidden',
  background: COLORS.bgRaiz,
};

const panelInfStyle = (alto: number): CSSProperties => ({
  height: `${alto}px`,
  minHeight: 0,
  overflow: 'auto',
  background: COLORS.bgPanel,
  borderTop: `1px solid ${COLORS.borde}`,
});

const barraEstadoStyle: CSSProperties = {
  minHeight: 0,
  overflow: 'hidden',
  background: COLORS.bgBarra,
  borderTop: `1px solid ${COLORS.borde}`,
  fontFamily: typography.fontFamily.mono,
};

// ============================================================================
// COMPONENT
// ============================================================================

export const WorkspaceShell: FC<WorkspaceShellProps> = ({
  principal,
  panelIzq,
  panelDer,
  panelInf,
  barraEstado,
  panelIzqVisible = true,
  panelDerVisible = true,
  panelInfVisible = true,
  anchoPanelIzq = ANCHO_PANEL_IZQ,
  anchoPanelDerInicial = ANCHO_PANEL_DER,
  altoPanelInf = ALTO_PANEL_INF,
  breakpointEstrecho = BREAKPOINT_ESTRECHO,
  alto = '100%',
  className,
}) => {
  // Responsividad por CSS (auto-ocultable); sin estado ni lógica JS.
  inyectarEstilosResponsive(breakpointEstrecho);

  const mostrarPanelIzq = panelIzqVisible && panelIzq != null;
  const mostrarPanelDer = panelDerVisible && panelDer != null;
  const mostrarPanelInf = panelInfVisible && panelInf != null;

  // Agarres de redimensionado (§11.3): ficha 280-500 px · tabla 140-480 px.
  const ficha = useDimensionArrastrable(
    'x',
    anchoPanelDerInicial,
    ANCHO_PANEL_DER_MIN,
    ANCHO_PANEL_DER_MAX,
  );
  const tabla = useDimensionArrastrable('y', altoPanelInf, ALTO_PANEL_INF_MIN, ALTO_PANEL_INF_MAX);

  const claseRaiz = className ? `${CLASE_RAIZ} ${className}` : CLASE_RAIZ;

  return (
    <div
      className={claseRaiz}
      data-ws-bp={breakpointEstrecho}
      data-testid="workspace-shell"
      style={raizStyle(alto)}
    >
      {/* Cuerpo: panel izq. acoplable + principal central + agarre + ficha (der.) */}
      <div
        className={CLASE_CUERPO}
        style={cuerpoStyle(anchoPanelIzq, mostrarPanelIzq, ficha.valor, mostrarPanelDer)}
      >
        {mostrarPanelIzq && (
          <aside
            className={CLASE_PANEL_IZQ}
            data-testid="workspace-panel-izq"
            style={panelIzqStyle}
          >
            {panelIzq}
          </aside>
        )}
        <main data-testid="workspace-mapa" style={principalStyle}>
          {principal}
        </main>
        {mostrarPanelDer && (
          <>
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Redimensionar ficha"
              data-testid="workspace-agarre-der"
              style={agarreStyle('x')}
              onPointerDown={ficha.onPointerDown}
            >
              <div style={agarreGripStyle('x')} />
            </div>
            <aside data-testid="workspace-panel-der" style={panelDerStyle}>
              {panelDer}
            </aside>
          </>
        )}
      </div>

      {/* Panel inferior acoplable (tabla canónica) con agarre superior */}
      {mostrarPanelInf && (
        <section
          data-testid="workspace-panel-inf"
          style={{ ...panelInfStyle(tabla.valor), display: 'flex', flexDirection: 'column' }}
        >
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label="Redimensionar tabla"
            data-testid="workspace-agarre-inf"
            style={agarreStyle('y')}
            onPointerDown={tabla.onPointerDown}
          >
            <div style={agarreGripStyle('y')} />
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{panelInf}</div>
        </section>
      )}

      {/* Ranura de barra de estado */}
      {barraEstado != null && (
        <footer data-testid="workspace-barra-estado" style={barraEstadoStyle}>
          {barraEstado}
        </footer>
      )}
    </div>
  );
};

export default WorkspaceShell;
